import React, { useMemo, useState } from "react";
import { GlobalButton, GlobalSelectField, GlobalTextField } from "../../../../global/forms/GlobalFormFields";

function countGranted(rows) {
  return (rows || []).filter(
    (r) =>
      Number(r.can_view) ||
      Number(r.can_create) ||
      Number(r.can_edit) ||
      Number(r.can_delete),
  ).length;
}

function IconShield() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
      <path d="M12 2l8 4v6c0 5-3.5 9.5-8 10-4.5-.5-8-5-8-10V6l8-4z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconGrid() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}

/** Shared shell for module / submodule permission matrices. */
export default function PermissionsWorkspace({
  id,
  variant = "module",
  title,
  lead,
  roleOptions,
  selectedRoleId,
  onRoleChange,
  rows,
  loading,
  saving,
  loadError,
  onSave,
  onGrantAllView,
  onClearAll,
  emptyMatrixMessage = null,
  children,
}) {
  const [query, setQuery] = useState("");

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (row) =>
        String(row._label ?? "").toLowerCase().includes(q) ||
        String(row._sublabel ?? "").toLowerCase().includes(q),
    );
  }, [rows, query]);

  const granted = countGranted(rows);
  const total = rows?.length ?? 0;
  const pct = total ? Math.round((granted / total) * 100) : 0;
  const icon = variant === "submodule" ? <IconGrid /> : <IconShield />;

  return (
    <section id={id} className={`ub-perm-workspace ub-perm-workspace--${variant}`}>
      <div className="ub-perm-workspace__glow" aria-hidden />
      <header className="ub-perm-workspace__hero">
        <div className="ub-perm-workspace__icon">{icon}</div>
        <div className="ub-perm-workspace__copy">
          <h2 className="ub-perm-workspace__title">{title}</h2>
          <p className="ub-perm-workspace__lead">{lead}</p>
        </div>
        <div
          className="ub-perm-workspace__meter"
          aria-label={`${granted} of ${total} items have permissions`}
        >
          <span className="ub-perm-workspace__meter-value">{pct}%</span>
          <span className="ub-perm-workspace__meter-label">
            {granted}/{total} active
          </span>
          <div className="ub-perm-workspace__meter-track">
            <div
              className="ub-perm-workspace__meter-fill"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </header>

      <div className="ub-perm-workspace__controls">
        <GlobalSelectField
          id={`${id}-role`}
          label="Role"
          value={selectedRoleId}
          onChange={onRoleChange}
          options={[{ value: "", label: "Select role" }, ...roleOptions]}
        />
        <GlobalTextField
          id={`${id}-search`}
          label="Filter"
          value={query}
          onChange={setQuery}
          placeholder="Search modules or screens…"
        />
        <div className="ub-perm-workspace__actions">
          <GlobalButton
            type="button"
            variant="ghost"
            size="sm"
            disabled={!selectedRoleId || saving || loading}
            onClick={onGrantAllView}
          >
            All view
          </GlobalButton>
          <GlobalButton
            type="button"
            variant="ghost"
            size="sm"
            disabled={!selectedRoleId || saving || loading}
            onClick={onClearAll}
          >
            Clear all
          </GlobalButton>
          <GlobalButton
            variant="primary"
            disabled={!selectedRoleId || saving || loading}
            onClick={onSave}
          >
            {saving ? "Saving…" : "Save permissions"}
          </GlobalButton>
        </div>
      </div>

      {loadError ? (
        <p className="ub-form-alert ub-form-alert--error" role="alert">
          {loadError}
        </p>
      ) : null}

      {!selectedRoleId ? (
        <div className="ub-perm-workspace__empty">
          <p className="ub-perm-workspace__empty-title">Pick a role to begin</p>
          <p className="ub-perm-workspace__empty-text">
            Choose a role above to load its permission matrix. Saving updates the
            sidebar menu for that role.
          </p>
        </div>
      ) : loading ? (
        <p className="ub-muted ub-perm-workspace__loading">Loading permissions…</p>
      ) : filteredRows.length === 0 ? (
        <p className="ub-muted ub-perm-workspace__loading">
          {emptyMatrixMessage ||
            (query.trim()
              ? "No items match your filter."
              : "No items to show.")}
        </p>
      ) : (
        <div className="ub-perm-workspace__matrix">{children(filteredRows)}</div>
      )}
    </section>
  );
}
